import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  CircularProgress,
  Snackbar,
  Alert,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
  Switch,
  FormControlLabel,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Tooltip,
  Card,
  CardContent,
} from '@mui/material';
import {
  AccountBalance as AccountBalanceIcon,
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  AccountBalanceWallet as WalletIcon,
  Schedule as ScheduleIcon,
  Warning as WarningIcon,
  PointOfSale as PointOfSaleIcon,
  Receipt as ReceiptIcon,
  MoneyOff as MoneyOffIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  CreditCard as CreditCardIcon,
  Payments as PaymentsIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import axios from '../../config/axios';
import { getApiUrl } from '../../config/environment';
import { usePageBreakpoints, pageContainerSx } from '../../hooks/useClinicPageBreakpoints';
import { useAuth } from '../../contexts/AuthContext';


const formatCurrency = (v: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);
const formatHiddenCurrency = () => 'R$ ••••••';

/** Limites do mês civil corrente (local), formato YYYY-MM-DD para input[type=date] / API. */
function getCurrentMonthBounds(): { from: string; to: string } {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();
  const pad = (n: number) => String(n).padStart(2, '0');
  const from = `${y}-${pad(m + 1)}-01`;
  const lastDay = new Date(y, m + 1, 0).getDate();
  const to = `${y}-${pad(m + 1)}-${pad(lastDay)}`;
  return { from, to };
}

function formatBrDate(iso: string): string {
  const [y, mo, d] = iso.split('-');
  if (!y || !mo || !d) return iso;
  return `${d}/${mo}/${y}`;
}

interface BankAccount {
  id: string;
  bank_name: string;
  bank_code: string;
  agency: string;
  account_number: string;
  account_type: string;
  holder_name: string;
  holder_cpf_cnpj: string;
  initial_balance: number;
  current_balance: number;
  is_active: boolean;
  is_default: boolean;
  notes: string;
}

const emptyAccount: Partial<BankAccount> = {
  bank_name: '',
  bank_code: '',
  agency: '',
  account_number: '',
  account_type: 'checking',
  holder_name: '',
  holder_cpf_cnpj: '',
  initial_balance: 0,
  is_active: true,
  is_default: false,
  notes: '',
};

const FinancialDashboard: React.FC = () => {
  const { isMobile } = usePageBreakpoints();
  const { user, availableUnits, currentUnit } = useAuth();
  const isMatrixAdmin =
    (user?.role === 'admin' || user?.role === 'admin_master') && (availableUnits?.length || 0) >= 2;

  const [dashboardData, setDashboardData] = useState<any>(null);
  const [bankAccounts, setBankAccounts] = useState<BankAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [unitFilter, setUnitFilter] = useState<string>('current');
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'error' });

  const [accountDialogOpen, setAccountDialogOpen] = useState(false);
  const [editingAccountId, setEditingAccountId] = useState<string | null>(null);
  const [accountForm, setAccountForm] = useState<Partial<BankAccount>>(emptyAccount);
  const [saving, setSaving] = useState(false);
  const [showBankBalances, setShowBankBalances] = useState(false);

  const showMessage = (message: string, severity: 'success' | 'error' = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const loadDashboardWithRange = useCallback(async (dateFrom: string, dateTo: string, unitOverride?: string) => {
    setLoading(true);
    try {
      const params: Record<string, string> = { date_from: dateFrom, date_to: dateTo };
      const effectiveUnit = unitOverride !== undefined ? unitOverride : unitFilter;
      if (isMatrixAdmin && effectiveUnit && effectiveUnit !== 'current') {
        params.unit_id = effectiveUnit;
      }
      const response = await axios.get(getApiUrl('/financial-module/dashboard'), { params });
      const d = response.data?.data || response.data;
      setDashboardData(d);
      if (d?.bank_accounts?.accounts) {
        setBankAccounts(d.bank_accounts.accounts);
      }
    } catch (error: any) {
      showMessage(error.response?.data?.error || 'Erro ao carregar resumo financeiro', 'error');
    } finally {
      setLoading(false);
    }
  }, [isMatrixAdmin, unitFilter]);

  const loadDashboard = useCallback(async () => {
    const hasCustom = filterDateFrom.trim() !== '' && filterDateTo.trim() !== '';
    const { from, to } = hasCustom
      ? { from: filterDateFrom, to: filterDateTo }
      : getCurrentMonthBounds();
    await loadDashboardWithRange(from, to);
  }, [filterDateFrom, filterDateTo, loadDashboardWithRange]);

  const handleFilterClick = () => {
    const hasFrom = filterDateFrom.trim() !== '';
    const hasTo = filterDateTo.trim() !== '';
    if (hasFrom !== hasTo) {
      showMessage('Para filtrar por período, preencha data de início e data de fim.', 'error');
      return;
    }
    void loadDashboard();
  };

  const clearDateFilter = () => {
    setFilterDateFrom('');
    setFilterDateTo('');
    const { from, to } = getCurrentMonthBounds();
    void loadDashboardWithRange(from, to);
  };

  const monthBounds = getCurrentMonthBounds();
  const usingCustomRange = filterDateFrom.trim() !== '' && filterDateTo.trim() !== '';
  const effectiveLabel = usingCustomRange
    ? `${formatBrDate(filterDateFrom)} a ${formatBrDate(filterDateTo)}`
    : `${formatBrDate(monthBounds.from)} a ${formatBrDate(monthBounds.to)} (mês atual)`;

  const loadBankAccounts = useCallback(async () => {
    try {
      const response = await axios.get(getApiUrl('/financial-module/bank-accounts'));
      setBankAccounts(response.data?.data || []);
    } catch {
      // fallback to dashboard data
    }
  }, []);

  // Somente montagem: evita re-fetch quando o usuário edita datas (picker nativo + re-render
  // durante o período da interação causa NotFoundError removeChild em alguns navegadores/Windows).
  useEffect(() => {
    const { from, to } = getCurrentMonthBounds();
    void loadDashboardWithRange(from, to);
  }, [loadDashboardWithRange]);

  const openNewAccount = () => {
    setEditingAccountId(null);
    setAccountForm({ ...emptyAccount });
    setAccountDialogOpen(true);
  };

  const openEditAccount = (account: BankAccount) => {
    setEditingAccountId(account.id);
    setAccountForm({ ...account });
    setAccountDialogOpen(true);
  };

  const handleSaveAccount = async () => {
    if (!accountForm.bank_name || !accountForm.account_number) {
      showMessage('Nome do banco e número da conta são obrigatórios', 'error');
      return;
    }
    setSaving(true);
    try {
      if (editingAccountId) {
        await axios.put(getApiUrl(`/financial-module/bank-accounts/${editingAccountId}`), accountForm);
        showMessage('Conta atualizada com sucesso');
      } else {
        await axios.post(getApiUrl('/financial-module/bank-accounts'), accountForm);
        showMessage('Conta cadastrada com sucesso');
      }
      setAccountDialogOpen(false);
      await loadBankAccounts();
      loadDashboard();
    } catch (error: any) {
      showMessage(error.response?.data?.error || 'Erro ao salvar conta', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAccount = async (id: string) => {
    if (!window.confirm('Excluir esta conta bancária?')) return;
    try {
      await axios.delete(getApiUrl(`/financial-module/bank-accounts/${id}`));
      showMessage('Conta excluída');
      await loadBankAccounts();
      loadDashboard();
    } catch (error: any) {
      showMessage(error.response?.data?.error || 'Erro ao excluir', 'error');
    }
  };

  const txn = dashboardData?.transactions || {};
  const reco = dashboardData?.receivables_open || {};
  const payo = dashboardData?.payables_open || {};
  const totalBankBalance = dashboardData?.bank_accounts?.total_balance || bankAccounts.reduce((s, a) => s + (parseFloat(String(a.current_balance)) || 0), 0);

  type KpiDef = {
    label: string;
    value: number;
    color: string;
    bg: string;
    icon: React.ReactNode;
    isCount?: boolean;
    extra?: string;
    sublabel?: string;
  };

  const receivableOpenCards: KpiDef[] = [
    { label: 'A receber — pendente', value: reco.pending_amount ?? 0, color: '#e65100', bg: '#fff3e0', icon: <ScheduleIcon />, sublabel: reco.count_pending != null ? `${reco.count_pending} título(s)` : undefined },
    { label: 'A receber — parcial (restante)', value: reco.partial_outstanding ?? 0, color: '#f57f17', bg: '#fffde7', icon: <PaymentsIcon />, sublabel: reco.count_partial != null ? `${reco.count_partial} título(s)` : undefined },
    { label: 'A receber — atrasado (restante)', value: reco.overdue_outstanding ?? 0, color: '#b71c1c', bg: '#fce4ec', icon: <WarningIcon />, sublabel: reco.count_overdue != null ? `${reco.count_overdue} título(s)` : undefined },
    { label: 'A receber — total em aberto', value: reco.total_outstanding ?? 0, color: '#2e7d32', bg: '#e8f5e9', icon: <TrendingUpIcon />, sublabel: reco.count_open != null ? `${reco.count_open} título(s)` : undefined },
  ];

  const payablesOpenCards: KpiDef[] = [
    { label: 'A pagar — pendente', value: payo.pending_amount ?? 0, color: '#c62828', bg: '#ffebee', icon: <TrendingDownIcon /> },
    { label: 'A pagar — parcial (restante)', value: payo.partial_outstanding ?? 0, color: '#ad1457', bg: '#fce4ec', icon: <PaymentsIcon /> },
    { label: 'A pagar — atrasado (restante)', value: payo.overdue_outstanding ?? 0, color: '#880e4f', bg: '#f8bbd0', icon: <WarningIcon /> },
    { label: 'A pagar — total em aberto', value: payo.total_outstanding ?? 0, color: '#5d4037', bg: '#efebe9', icon: <MoneyOffIcon /> },
  ];

  const periodFlowCards: KpiDef[] = [
    { label: 'Receitas (lançadas no período)', value: txn.total_charged || 0, color: '#2e7d32', bg: '#e8f5e9', icon: <TrendingUpIcon /> },
    { label: 'Despesas (lançadas no período)', value: txn.expenses || 0, color: '#c62828', bg: '#ffebee', icon: <TrendingDownIcon /> },
    { label: 'Saldo operacional (período)', value: txn.balance || 0, color: '#1565c0', bg: '#e3f2fd', icon: <WalletIcon /> },
    { label: 'Recebido — CR quitadas no período', value: txn.charges_received ?? 0, color: '#00838f', bg: '#e0f7fa', icon: <ReceiptIcon /> },
  ];

  const otherKpiCards: KpiDef[] = [
    { label: 'Caixa Aberto', value: dashboardData?.cash_register?.opening_balance || 0, color: '#00695c', bg: '#e0f2f1', icon: <PointOfSaleIcon />, extra: dashboardData?.cash_register ? 'Aberto' : 'Fechado' },
    { label: 'Cheques Pendentes', value: dashboardData?.pending_checks || 0, color: '#4e342e', bg: '#efebe9', icon: <ReceiptIcon /> },
    { label: 'Baixas Cartão Pend.', value: dashboardData?.pending_settlements || 0, color: '#6a1b9a', bg: '#f3e5f5', icon: <CreditCardIcon /> },
    { label: 'Descontos Pendentes', value: dashboardData?.pending_discounts || 0, color: '#ef6c00', bg: '#fff8e1', icon: <MoneyOffIcon />, isCount: true },
    { label: 'Contas atrasadas (pagar — critério venc.)', value: dashboardData?.overdue_payables || 0, color: '#d32f2f', bg: '#ffcdd2', icon: <WarningIcon /> },
  ];

  const renderKpiSection = (title: string, subtitle: string | undefined, cards: KpiDef[]) => (
    <>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>{title}</Typography>
      {subtitle && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {subtitle}
        </Typography>
      )}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {cards.map((card, idx) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={`${title}-${idx}`}>
            <Paper
              sx={{ p: 2, borderRadius: 2, borderLeft: `4px solid ${card.color}`, display: 'flex', alignItems: 'center', gap: 2, transition: 'box-shadow 0.2s', '&:hover': { boxShadow: 4 } }}
              elevation={1}
            >
              <Box sx={{ width: 44, height: 44, borderRadius: '50%', bgcolor: card.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', color: card.color, flexShrink: 0 }}>
                {card.icon}
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" color="text.secondary">{card.label}</Typography>
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: card.color, lineHeight: 1.2 }}>
                  {card.isCount ? card.value : formatCurrency(card.value)}
                </Typography>
                {card.sublabel && (
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
                    {card.sublabel}
                  </Typography>
                )}
                {card.extra && <Chip label={card.extra} size="small" sx={{ mt: 0.5, height: 20, fontSize: '0.7rem' }} color={dashboardData?.cash_register ? 'success' : 'default'} />}
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>
    </>
  );

  if (loading && !dashboardData) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={pageContainerSx}>
      {/* Header */}
      <Box sx={{
        mb: { xs: 2, md: 3 },
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between',
        alignItems: { xs: 'stretch', sm: 'center' },
        gap: 2,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
          <AccountBalanceIcon sx={{ fontSize: 32, color: '#0d9488' }} />
          <Typography variant={isMobile ? 'h6' : 'h5'} sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>Resumo Financeiro</Typography>
        </Box>
        <Button variant="outlined" fullWidth={isMobile} startIcon={<RefreshIcon />} onClick={loadDashboard} disabled={loading} sx={{ textTransform: 'none', flexShrink: 0 }}>
          Atualizar
        </Button>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Indicadores no período: <strong>{effectiveLabel}</strong>
          {!usingCustomRange && ' — deixe as datas em branco ou use «Mês atual» para manter o mês civil corrente.'}
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4} md={3}>
            <TextField
              label="Data Início (opcional)"
              type="date"
              fullWidth
              size="small"
              value={filterDateFrom}
              onChange={(e) => setFilterDateFrom(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={4} md={3}>
            <TextField
              label="Data Fim (opcional)"
              type="date"
              fullWidth
              size="small"
              value={filterDateTo}
              onChange={(e) => setFilterDateTo(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={4} md={3}>
            <Button variant="contained" onClick={handleFilterClick} disabled={loading} fullWidth sx={{ textTransform: 'none' }}>
              Filtrar
            </Button>
          </Grid>
          <Grid item xs={12} sm={4} md={3}>
            <Button variant="outlined" onClick={clearDateFilter} disabled={loading} fullWidth sx={{ textTransform: 'none' }}>
              Mês atual
            </Button>
          </Grid>
          {isMatrixAdmin && (
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel id="unit-filter-label">Unidade</InputLabel>
                <Select
                  labelId="unit-filter-label"
                  label="Unidade"
                  value={unitFilter}
                  onChange={(e) => {
                    const v = String(e.target.value);
                    setUnitFilter(v);
                    const { from, to } = getCurrentMonthBounds();
                    const hasCustom = filterDateFrom && filterDateTo;
                    void loadDashboardWithRange(
                      hasCustom ? filterDateFrom : from,
                      hasCustom ? filterDateTo : to,
                      v,
                    );
                  }}
                  MenuProps={{ disablePortal: true }}
                >
                  <MenuItem value="current">Unidade atual ({currentUnit?.short_name || '-'})</MenuItem>
                  <MenuItem value="all">Todas as unidades (consolidado)</MenuItem>
                  {availableUnits.map((u) => (
                    <MenuItem key={u.id} value={u.id}>
                      {u.name} {u.is_matrix ? '(Matriz)' : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
        </Grid>
      </Paper>

      {/* ══════════════ SALDO BANCÁRIO TOTAL ══════════════ */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 2, background: 'linear-gradient(135deg, #1a237e 0%, #283593 100%)', color: '#fff' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <AccountBalanceIcon sx={{ fontSize: 28 }} />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>Saldo em Bancos</Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title={showBankBalances ? 'Ocultar saldos' : 'Mostrar saldos'}>
              <IconButton
                size="small"
                onClick={() => setShowBankBalances((v) => !v)}
                sx={{ color: '#fff', border: '1px solid rgba(255,255,255,0.5)' }}
              >
                {showBankBalances ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
            <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={openNewAccount}
              sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.5)', '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.1)' } }}>
              Nova Conta
            </Button>
          </Box>
        </Box>
        <Typography
          variant="h3"
          sx={{
            fontWeight: 800,
            mb: 1,
            filter: showBankBalances ? 'none' : 'blur(7px)',
            userSelect: 'none',
            transition: 'filter 0.2s ease',
          }}
        >
          {showBankBalances ? formatCurrency(totalBankBalance) : formatHiddenCurrency()}
        </Typography>
        <Typography variant="body2" sx={{ opacity: 0.8 }}>{bankAccounts.length} conta{bankAccounts.length !== 1 ? 's' : ''} cadastrada{bankAccounts.length !== 1 ? 's' : ''}</Typography>
      </Paper>

      {/* ══════════════ CONTAS BANCÁRIAS INDIVIDUAIS ══════════════ */}
      {bankAccounts.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Grid container spacing={2}>
            {bankAccounts.map((account) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={account.id}>
                <Card variant="outlined" sx={{ borderRadius: 2, borderLeft: account.is_default ? '4px solid #1565c0' : '4px solid #e0e0e0', transition: 'box-shadow 0.2s', '&:hover': { boxShadow: 3 } }}>
                  <CardContent sx={{ pb: '12px !important' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          {account.is_default && <Tooltip title="Conta padrão"><StarIcon sx={{ fontSize: 16, color: '#f9a825' }} /></Tooltip>}
                          <Typography variant="subtitle2" sx={{ fontWeight: 700 }} noWrap>{account.bank_name}</Typography>
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          {account.account_type === 'checking' ? 'Corrente' : 'Poupança'} • {account.agency ? `Ag ${account.agency} / ` : ''}Cc {account.account_number}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', ml: 1 }}>
                        <IconButton size="small" onClick={() => openEditAccount(account)}><EditIcon sx={{ fontSize: 16 }} /></IconButton>
                        <IconButton size="small" onClick={() => handleDeleteAccount(account.id)}><DeleteIcon sx={{ fontSize: 16 }} /></IconButton>
                      </Box>
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: parseFloat(String(account.current_balance)) >= 0 ? '#2e7d32' : '#c62828' }}>
                      <Box
                        component="span"
                        sx={{
                          display: 'inline-block',
                          filter: showBankBalances ? 'none' : 'blur(6px)',
                          userSelect: 'none',
                          transition: 'filter 0.2s ease',
                        }}
                      >
                        {showBankBalances ? formatCurrency(parseFloat(String(account.current_balance)) || 0) : formatHiddenCurrency()}
                      </Box>
                    </Typography>
                    {account.holder_name && <Typography variant="caption" color="text.secondary" noWrap>{account.holder_name}</Typography>}
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      <Divider sx={{ mb: 3 }} />

      {/* ══════════════ KPI CARDS ══════════════ */}
      {renderKpiSection(
        'Contas a receber (em aberto)',
        'Posição atual da carteira — não depende do filtro de período acima.',
        receivableOpenCards,
      )}
      {renderKpiSection(
        'Contas a pagar (em aberto)',
        'Posição atual — não depende do filtro de período acima.',
        payablesOpenCards,
      )}
      {renderKpiSection(
        'Fluxo no período',
        `Valores conforme o período: ${effectiveLabel}.`,
        periodFlowCards,
      )}
      {renderKpiSection('Operacional e alertas', undefined, otherKpiCards)}

      {/* ══════════════ DIALOG - CADASTRO/EDIÇÃO CONTA BANCÁRIA ══════════════ */}
      <Dialog fullScreen={isMobile} scroll="paper" open={accountDialogOpen} onClose={() => setAccountDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingAccountId ? 'Editar Conta Bancária' : 'Nova Conta Bancária'}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={8}>
              <TextField label="Nome do Banco *" fullWidth size="small" value={accountForm.bank_name || ''} onChange={(e) => setAccountForm({ ...accountForm, bank_name: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField label="Código Banco" fullWidth size="small" value={accountForm.bank_code || ''} onChange={(e) => setAccountForm({ ...accountForm, bank_code: e.target.value })} placeholder="Ex: 001" />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField label="Agência" fullWidth size="small" value={accountForm.agency || ''} onChange={(e) => setAccountForm({ ...accountForm, agency: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField label="Nº Conta *" fullWidth size="small" value={accountForm.account_number || ''} onChange={(e) => setAccountForm({ ...accountForm, account_number: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Tipo</InputLabel>
                <Select
                  value={accountForm.account_type || 'checking'}
                  label="Tipo"
                  onChange={(e) => setAccountForm({ ...accountForm, account_type: e.target.value })}
                  MenuProps={{ disablePortal: true }}
                >
                  <MenuItem value="checking">Corrente</MenuItem>
                  <MenuItem value="savings">Poupança</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Titular" fullWidth size="small" value={accountForm.holder_name || ''} onChange={(e) => setAccountForm({ ...accountForm, holder_name: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="CPF/CNPJ Titular" fullWidth size="small" value={accountForm.holder_cpf_cnpj || ''} onChange={(e) => setAccountForm({ ...accountForm, holder_cpf_cnpj: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Saldo Inicial" fullWidth size="small" type="number" value={accountForm.initial_balance || 0}
                onChange={(e) => setAccountForm({ ...accountForm, initial_balance: parseFloat(e.target.value) || 0 })}
                inputProps={{ step: '0.01' }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box sx={{ display: 'flex', gap: 2, pt: 0.5 }}>
                <FormControlLabel control={<Switch checked={accountForm.is_active ?? true} onChange={(e) => setAccountForm({ ...accountForm, is_active: e.target.checked })} />} label="Ativa" />
                <FormControlLabel control={<Switch checked={accountForm.is_default ?? false} onChange={(e) => setAccountForm({ ...accountForm, is_default: e.target.checked })} />} label="Padrão" />
              </Box>
            </Grid>
            <Grid item xs={12}>
              <TextField label="Observações" fullWidth size="small" multiline rows={2} value={accountForm.notes || ''} onChange={(e) => setAccountForm({ ...accountForm, notes: e.target.value })} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAccountDialogOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleSaveAccount} disabled={saving}>{saving ? 'Salvando...' : 'Salvar'}</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: isMobile ? 'center' : 'right' }}
      >
        <Alert
          key={`${snackbar.severity}:${snackbar.message}`}
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default FinancialDashboard;
